import React, { Fragment, useEffect, useState } from 'react'
import { ModalHeader, ModalBody, ModalFooter } from '../../Modal/Modal'
import Button from '../../Button/Button'
import ButtonGroup from '../../Button/ButtonGroup/ButtonGroup'
import Notification from '../../Notification/Notification'
import Droppable from './Upload/Dropable'
import { Upload } from './Upload'
import '../style.css'
import { UploadAssetModalProps } from '../types'
import { CustomAction, OnCustomActionClick } from '../types/UploadAssetModalProps'

function isJson(str: string) {
  try {
    JSON.parse(str)
  } catch (e) {
    return false
  }
  return true
}

export const getImageMarkdown = (href, text = 'Enter your image caption here') => {
  text = text === '' ? href : text
  return `![${text}](${href})`
}

const UploadModal = (props: UploadAssetModalProps) => {
  const { closeModal, onSubmit, getFolders, createFolder, upload, getUsers, getFilesFromFileSystem = true, actions } = props;

  const [folderUid, setFolderUid] = useState("cs_root");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<Record<string, any>>({});
  const [customActionLoadingIndex, setCustomActionLoadingIndex] = useState<number | null>(null);

  const fileAccept = props?.fileTypes?.split(',')?.map((ele) => `.${ele}`) || [];
  if (props?.only) {
    fileAccept.push(`${props.only}/*`);
  }

  useEffect(() => {
    getUsers()
      .then(res => setUsers(res))
      .catch(console.error)
  }, [])

  const handleReset = () => {
    if (closeModal) {
      closeModal();
    }
  };

  const handleError = (errorType: string) => {
    let message = '';
    if (errorType === 'size') {
      message = `The file size should be within ${props?.size?.min || 0} MB and ${props?.size?.max || 700
        } MB.`;
    } else if (errorType === 'maxSelect') {
      message = 'Maximum 10 files can be uploaded.';
    }
    Notification({
      displayContent: {
        text: message,
      },
      notifyProps: { hideProgressBar: true, position: 'bottom-center' },
      type: 'error',
    });
    setLoading(false);
  };

  const handleUpload = (files, rejectedFilesDueToSize = []) => {
    const showError = {
      maxSelect: false,
      size: false,
    };
    let rejectedFilesDueToMaxLimit = [];
    if (typeof props.multiple !== "boolean" && files.length > props?.multiple?.max) {
      showError.maxSelect = true;
      rejectedFilesDueToMaxLimit = files.slice(props.multiple.max);
      files = files.slice(0, props.multiple.max);
    }
    if (rejectedFilesDueToSize?.length) {
      showError.size = true;
    }

    const folder = folderUid === 'cs_root' ? '' : folderUid;
    setLoading(true);
    if (!files?.length) {
      handleError('size');
      onSubmit(
        [],
        rejectedFilesDueToSize.map((file) => ({
          message: `File size should be within ${props?.size?.min || 0} MB and ${props?.size?.max || 700
            } MB.`,
          code: 'sizeLimit',
          file: file.file,
        }))
      );
      handleReset();
      return;
    }
    upload({ files, options: { parentFolderUid: folder } })
      .then((res: Record<string, any>) => {
        if (onSubmit) {
          if (isJson(res?.data)) {
            const error = JSON.parse(res?.data)?.data;
            setLoading(false);
            handleReset();
            Notification({
              displayContent: { text: error.error_message },
              notifyProps: { hideProgressBar: true, position: 'bottom-center' },
              type: 'error',
            });
            onSubmit(
              [],
              files.map((file) => ({ file, message: error.error_message }))
            );
            return;
          }
          const responseAssets = Array.from(res?.data)?.map((val: any) => {
            const asset = val?.asset ? val.asset : {};
            return {
              ...asset,
              // these duplicated camel-cased keys are for backward compatibility
              // Note - the filename key is inconsistent between the select and upload modals
              // but that is how it was before the refactor
              filename: asset.filename,
              fileSize: asset.file_size,
              contentType: asset.content_type,
            };
          });
          onSubmit(responseAssets, [
            ...rejectedFilesDueToMaxLimit.map((file) => ({
              message: 'Max upload limit reached',
              code: 'maxSelectionLimit',
              file,
            })),
            ...rejectedFilesDueToSize.map((file) => ({
              message: `File size should be within ${props?.size?.min || 0} MB and ${props?.size?.max || 700
                } MB.`,
              code: 'sizeLimit',
              file: file.file,
            })),
          ]);
        }
        if (showError.maxSelect)
          Notification({
            displayContent: {
              text: 'Too many files selected. One or more files are ignored while uploading.',
            },
            notifyProps: { hideProgressBar: true, position: 'bottom-center' },
            type: 'error',
          });
        if (showError.size) {
          Notification({
            displayContent: {
              text: `${props?.multiple
                ? 'One or more files are ignored while uploading because the'
                : 'The'
                }file size should be within ${props?.size?.min || 0} MB and ${props?.size?.max || 700} MB.`,
            },
            notifyProps: { hideProgressBar: true, position: 'bottom-center' },
            type: 'error',
          });
        }
        setLoading(false);
        handleReset();
      })
      .catch((err) => {
        setLoading(false);
        handleReset();
      });
  };

  const handleCustomActionClick = (onClick: OnCustomActionClick, index: number) => {
    setCustomActionLoadingIndex(index);
    onClick({
      folderUid,
      stopLoading: () => setCustomActionLoadingIndex(null),
      closeModal: () => handleReset()
    });
  }

  return (
    <div className="asset-picker">
      <div id="scrte-image-modal">
        <ModalHeader title="Select a folder to upload asset(s)" closeModal={handleReset} />

        <ModalBody className="modalBodyCustomClass">
          <Fragment>
            <Upload
              multiple={props.multiple}
              size={props.size}
              fileTypes={fileAccept}
              getFolders={getFolders}
              createFolder={createFolder}
              users={users}
              handleUpload={handleUpload}
              setFolderUid={setFolderUid}
              getFilesFromFileSystem={getFilesFromFileSystem}
            />
          </Fragment>
        </ModalBody>

        <ModalFooter>

          {
            getFilesFromFileSystem ? (
              <ButtonGroup>
                <Button onClick={handleReset} buttonType="light">
                  Cancel
                </Button>
                <Droppable
                  config={{
                    noDrag: true,
                    multiple: props?.multiple,
                    onDrop: handleUpload,
                    ...(props?.size?.max && { maxSize: props?.size?.max * (1024 * 1024) }),
                    ...(props?.size?.min && { minSize: props?.size?.min * (1024 * 1024) })
                  }}
                  nativeConfig={{ accept: fileAccept.join() }}>
                  <Button icon='Upload' loading={loading}>Upload Here</Button>
                </Droppable>
              </ButtonGroup>
            ) : (
              <div className='folder-selector-button-grp'>
                <ButtonGroup>
                  <Button onClick={handleReset} buttonType="light">
                    Cancel
                  </Button>
                  {
                    actions?.map(({ label, onClick, restButtonProps }: CustomAction, index: number) => (
                      <Button
                        loading={index === customActionLoadingIndex}
                        onClick={() => handleCustomActionClick(onClick, index)}
                        {...restButtonProps}
                      >
                        {label}
                      </Button>
                    ))
                  }
                </ButtonGroup>
              </div>
            )
          }

        </ModalFooter>
      </div>
    </div>
  )
}

export default UploadModal;